/**
 * Session Recorder
 * Records user sessions using rrweb with segment-based approach
 */

import { record } from '@rrweb/record';
import type { eventWithTime } from '@rrweb/types';
import { BaseCollector } from './BaseCollector.js';
import type { EventBus } from '../core/EventBus.js';
import type { SessionSegment } from '../types.js';
import { RRWebPrivacyConfig } from '../strategies/privacy/RRWebPrivacyConfig.js';

// rrweb event type mapping
const RRWEB_EVENT_TYPES: Record<number, string> = {
  0: 'DomContentLoaded',
  1: 'Load',
  2: 'FullSnapshot',
  3: 'IncrementalSnapshot',
  4: 'Meta',
  5: 'Custom'
};

export class SessionRecorder extends BaseCollector {
  private privacyConfig: RRWebPrivacyConfig;
  private maxRecordingTime: number;
  private segmentDuration: number;
  private maxSegmentSize: number;

  // Recording state
  private isRecording: boolean = false;
  private recordingStartTime: number = 0;
  private stopRecordingFn: (() => void) | null = null;

  // Segment state
  private currentSegment: SessionSegment | null = null;
  private segmentTimer: number | null = null;

  constructor(
    eventBus: EventBus,
    sessionId: string,
    userId: string | null,
    privacyConfig: RRWebPrivacyConfig,
    config: {
      maxRecordingTime: number;
      segmentDuration: number;
      maxSegmentSize: number;
      debug: boolean;
    }
  ) {
    super('SessionRecorder', eventBus, sessionId, userId, config.debug);
    this.privacyConfig = privacyConfig;
    this.maxRecordingTime = config.maxRecordingTime;
    this.segmentDuration = config.segmentDuration;
    this.maxSegmentSize = config.maxSegmentSize;
  }

  initialize(): void {
    this.log('Initializing session recording');
    this.startRecording();
    this.isInitialized = true;
  }

  private startRecording(): void {
    if (this.isRecording) return;

    const rrwebConfig = this.privacyConfig.getRRWebConfig();

    this.stopRecordingFn = record({
      emit: (event) => this.onRecordingEvent(event),
      ...rrwebConfig
    }) || null;

    this.isRecording = true;
    this.recordingStartTime = Date.now();

    // Initialize first segment
    this.currentSegment = {
      events: [],
      startTime: Date.now(),
      endTime: null
    };

    // Schedule first segment flush
    this.scheduleSegmentFlush();

    this.log('Session recording started');
  }

  private stopRecording(): void {
    if (!this.isRecording) return;

    // Flush any remaining segment data
    if (this.currentSegment && this.currentSegment.events.length > 0) {
      this.flushCurrentSegment();
    }

    // Clear segment timer
    if (this.segmentTimer) {
      clearTimeout(this.segmentTimer);
      this.segmentTimer = null;
    }

    if (this.stopRecordingFn) {
      this.stopRecordingFn();
      this.stopRecordingFn = null;
    }

    this.isRecording = false;
    this.currentSegment = null;

    this.log('Session recording stopped');
  }

  private onRecordingEvent(event: eventWithTime): void {
    if (!this.isRecording) return;

    // Initialize new segment if needed
    if (!this.currentSegment) {
      this.currentSegment = {
        events: [],
        startTime: Date.now(),
        endTime: null
      };
      this.scheduleSegmentFlush();
    }

    // Add event to current segment
    this.currentSegment.events.push(event);

    this.log(`Recorded event: ${event.type}`, {
      timestamp: event.timestamp,
      segmentSize: this.currentSegment.events.length
    });

    // Force flush if segment gets too large
    if (this.currentSegment.events.length >= this.maxSegmentSize) {
      this.log('Segment size limit reached, flushing early');
      this.flushCurrentSegment();
    }

    // Stop recording if max time exceeded
    if (Date.now() - this.recordingStartTime > this.maxRecordingTime) {
      this.stopRecording();
      this.log('Recording stopped: max time exceeded');
    }
  }

  private scheduleSegmentFlush(): void {
    if (this.segmentTimer) {
      clearTimeout(this.segmentTimer);
    }

    this.segmentTimer = window.setTimeout(() => {
      this.flushCurrentSegment();
    }, this.segmentDuration);
  }

  private flushCurrentSegment(): void {
    if (!this.currentSegment || this.currentSegment.events.length === 0) {
      // Start new segment timer even if nothing to flush
      if (this.isRecording) {
        this.scheduleSegmentFlush();
      }
      return;
    }

    // Close the segment
    this.currentSegment.endTime = Date.now();

    this.log('Flushing segment', {
      eventCount: this.currentSegment.events.length,
      duration: this.currentSegment.endTime - this.currentSegment.startTime
    });

    // Convert rrweb events to our event format
    const baseMetadata = this.getBaseMetadata();
    
    this.currentSegment.events.forEach(rrwebEvent => {
      const sessionEvent = {
        ...baseMetadata,
        event_category: 'session' as const,
        event_type: 'session_recording' as const,
        rrweb_type: rrwebEvent.type,
        rrweb_type_name: RRWEB_EVENT_TYPES[rrwebEvent.type] || `Unknown(${rrwebEvent.type})`,
        rrweb_data_json: JSON.stringify(rrwebEvent.data),
        segment_start: this.currentSegment!.startTime,
        segment_end: this.currentSegment!.endTime || Date.now(),
        segment_duration: (this.currentSegment!.endTime || Date.now()) - this.currentSegment!.startTime,
        segment_event_count: this.currentSegment!.events.length,
        recording_start_time: this.recordingStartTime
      };

      this.emit('event:collected', sessionEvent);
    });

    // Start new segment
    this.currentSegment = {
      events: [],
      startTime: Date.now(),
      endTime: null
    };

    // Schedule next flush
    if (this.isRecording) {
      this.scheduleSegmentFlush();
    }
  }

  /**
   * Add a custom event to the recording
   */
  addCustomEvent(name: string, data: Record<string, any>): void {
    if (!this.isRecording) return;

    const customEvent: eventWithTime = {
      type: 5, // Custom event type in rrweb
      timestamp: Date.now(),
      data: {
        tag: 'custom',
        payload: { name, data }
      }
    };

    this.onRecordingEvent(customEvent);
  }

  /**
   * Pause recording
   */
  pause(): void {
    if (this.isRecording) {
      this.stopRecording();
      this.log('Recording paused');
    }
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (!this.isRecording) {
      this.startRecording();
      this.log('Recording resumed');
    }
  }

  destroy(): void {
    this.stopRecording();
    this.isInitialized = false;
    this.log('Destroyed');
  }
}

